import { Component, EventEmitter, Input, Output, inject } from '@angular/core';
import { FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { CompaniesApi, TenantUsersDto, UpdateCompanyInfoDto } from '@buspro/web/apis';
import { TenantAuthService } from '@buspro/web/core';
import { FeFormMessagesService, FormMessageComponent } from '@fulleffect/ngx-tailings';
import { injectMutation } from '@tanstack/angular-query-experimental';
import { NzFormControlComponent, NzFormDirective, NzFormItemComponent, NzFormLabelComponent } from 'ng-zorro-antd/form';
import { NzInputDirective } from 'ng-zorro-antd/input';
import { NzModalComponent, NzModalContentDirective, NzModalTitleDirective } from 'ng-zorro-antd/modal';
import { NzNotificationService } from 'ng-zorro-antd/notification';
import { lastValueFrom } from 'rxjs';

@Component({
  selector: 'tenant-update-company-info-modal',
  templateUrl: './update-company-info.component.html',
  styleUrls: ['./update-company-info.component.scss'],
  imports: [
    NzModalComponent,
    NzModalTitleDirective,
    NzModalContentDirective,
    ReactiveFormsModule,
    NzFormItemComponent,
    NzFormLabelComponent,
    NzFormControlComponent,
    NzInputDirective,
    NzFormDirective,
    FormMessageComponent,
  ],
  standalone: true,
})
export class UpdateCompanyInfoModalComponent {
  private formBuilder = inject(FormBuilder);
  private companiesApi = inject(CompaniesApi);
  private notification = inject(NzNotificationService);
  private feMessages = inject(FeFormMessagesService);
  private authService = inject(TenantAuthService);

  @Input() show = false;
  @Output() hide = new EventEmitter(true);

  form = this.formBuilder.group({
    phoneNumber: ['', [Validators.required]],
    email: ['', [Validators.required, Validators.email]],
    websiteUrl: ['', [Validators.required, Validators.pattern(/^https?:\/\/.+/)]],
    motto: ['', [Validators.required]],
  });

  private _user!: TenantUsersDto;

  get user(): TenantUsersDto {
    return this._user;
  }

  @Input()
  set user(value: TenantUsersDto) {
    if (value) {
      this._user = value;
      this.form.patchValue({
        phoneNumber: value.company?.phoneNumber || '',
        email: value.company?.email || '',
        websiteUrl: value.company?.websiteUrl || '',
        motto: value.company?.motto || '',
      });
    }
  }

  updateCompanyInfoMutation = injectMutation(() => ({
    mutationFn: (payload: UpdateCompanyInfoDto) =>
      lastValueFrom(this.companiesApi.companyControllerUpdateCompanyInfo({ body: payload })),
    onSuccess: () => {
      this.notification.success('Changes applied', 'Company information updated successfully');
      this.hide.emit();
      window.location.reload();
    },
    onError: (error) => {
      this.notification.error('Oops! Something went wrong', error.name);
    },
  }));

  onHide() {
    this.hide.emit(true);
    this.show = false;
  }

  update() {
    this.feMessages.emitError([]);
    const payload: UpdateCompanyInfoDto = {
      phoneNumber: this.form.get('phoneNumber')!.value!,
      email: this.form.get('email')!.value!,
      websiteUrl: this.form.get('websiteUrl')!.value!,
      motto: this.form.get('motto')!.value!,
    };

    this.updateCompanyInfoMutation.mutate(payload);
  }
}
